import React, { useState, useEffect, useRef } from "react";
import { useSnackbar } from "components/GlobalSnackbar";
import { handleApiError } from "utils/errorHandler";
import {
  Grid,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  MenuItem,
  Select,
  Box,
  FormControl,
  InputLabel,
  IconButton,
  Menu,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import MDBox from "components/MDBox";
import PrintIcon from "@mui/icons-material/Print";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import { buttonStyles } from "utils/buttonStyles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "../examples/Tables/DataTable";
import CustomPagination from "components/CustomPagination";
import TambahHarta from "components/STPopup/TambahHarta";
import EditHarta from "components/STPopup/EditHarta";
import HartaSearch from "components/STSearch/HartaSearch";
import { useMaterialUIController } from "context";
import {
  apiGetAllHarta,
  apiCreateHarta,
  apiUpdateHarta,
  apiDeleteHarta,
  apiSearchHarta,
  apiGetHartaForPrint,
} from "api/harta";
import printHartaPreview from "components/STPrint/PrintHarta";
import { autoCapitalizePayload } from "utils/autoCapitalize";

// Data
import dataHarta from "./data/harta";

export default function SenaraiHarta() {
  const { showSuccess, showError } = useSnackbar();
  // ...full code from SengaraiHarta.js...
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState({
    kod_harta: "",
    zon: "",
    kategori_kutipan: "",
  });
  const [openTambah, setOpenTambah] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed to 10 items per page

  // Column visibility state with localStorage persistence
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('hartaColumnVisibility');
    return saved ? JSON.parse(saved) : {
      kodharta: true,
      alamat: true,
      zonharta: true,
      kategorikutipan: true,
      kadarsewa: true,
    };
  });

  // Column menu state
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { rowId, column, value }
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Print dialog state
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState([]);
  const [printLoading, setPrintLoading] = useState(false);
  const [printZon, setPrintZon] = useState("");
  const [printKategori, setPrintKategori] = useState("");

  // Dynamic dropdown options for Zon and Kategori Kutipan
  const [zonOptions, setZonOptions] = useState([]);
  const [kategoriOptions, setKategoriOptions] = useState([]);

  // Debounced search state to avoid tight update loops while typing
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const searchDebounceRef = useRef();

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('hartaColumnVisibility', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Column visibility handlers
  const handleColumnMenuOpen = (event) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Inline editing handlers
  const handleCellEdit = (rowId, column, currentValue, row) => {
    setEditingCell({ rowId, column, value: currentValue, row });
    setEditValue(currentValue);
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    if (!editingCell) return;

    // Validation
    if (editingCell.column === 'kadar_sewa') {
      const numValue = parseFloat(editValue);
      if (isNaN(numValue)) {
        showError('Kadar sewa mesti nombor yang sah');
        return;
      }
      if (numValue <= 0) {
        showError('Kadar sewa mesti lebih besar daripada 0');
        return;
      }
    }

    if (!editValue || (typeof editValue === 'string' && editValue.trim() === '')) {
      showError('Nilai tidak boleh kosong');
      return;
    }

    setSaving(true);
    try {
      // Backend requires ALL fields for update, so merge the changed field with existing row data
      const fieldMap = {
        kadar_sewa: 'kadar_sewa',
        zon: 'zon',
        kategori_kutipan: 'kategori_kutipan',
      };

      const fieldName = fieldMap[editingCell.column];
      
      // Get the complete row data from editingCell.row and update only the changed field
      const updateData = {
        kod_harta: editingCell.row.kod_harta,
        zon: editingCell.row.zon,
        alamat1_harta: editingCell.row.alamat1_harta || '',
        alamat2_harta: editingCell.row.alamat2_harta || '',
        poskod_harta: editingCell.row.poskod_harta || '',
        bandar_harta: editingCell.row.bandar_harta || '',
        negeri_harta: editingCell.row.negeri_harta || '',
        kadar_sewa: editingCell.row.kadar_sewa,
        bahagian_pemantau: editingCell.row.bahagian_pemantau,
        kategori_kutipan: editingCell.row.kategori_kutipan,
        // Override with the edited value
        [fieldName]: editValue
      };

      await apiUpdateHarta(editingCell.row.id, updateData);
      showSuccess('Rekod berjaya dikemaskini');
      setEditingCell(null);
      setEditValue('');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setSaving(false);
    }
  };

  // Keyboard handlers for inline editing
  const handleEditKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancelEdit();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    }
  };

  // Search change handler (no debounce here, debounce is in useEffect)
  const handleSearchChange = (e) => {
    setSearch({ ...search, [e.target.name]: e.target.value });
  };

  // Debounce search to reduce fetch churn and prevent nested updates
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  // When debounced search changes, reset to page 1 if needed
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [debouncedSearch]);

  // Fetch unique Zon and Kategori Kutipan from backend
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await apiGetHartaForPrint({});
        const data = res.data || [];
        // Get unique zon and kategori_kutipan values
        const zonSet = new Set();
        const kategoriSet = new Set();
        data.forEach((row) => {
          if (row.zon && row.zon.trim()) zonSet.add(row.zon.trim());
          if (row.kategori_kutipan && row.kategori_kutipan.trim()) kategoriSet.add(row.kategori_kutipan.trim());
        });
        const zonArray = Array.from(zonSet).sort();
        const kategoriArray = Array.from(kategoriSet).sort();
        setZonOptions(zonArray.length > 0 ? zonArray : []);
        setKategoriOptions(kategoriArray.length > 0 ? kategoriArray : []);
        console.log('Dropdown options loaded:', { zonOptions: zonArray, kategoriOptions: kategoriArray });
      } catch (e) {
        console.error('Error fetching dropdown options:', e);
        setZonOptions([]);
        setKategoriOptions([]);
      }
    };
    fetchDropdowns();
  }, []);

  // Print dialog open handler (no preview, just dropdowns)
  const handlePrintClick = () => {
    setPrintDialogOpen(true);
  };

  // Print action handler: fetch data, generate printable HTML, and open in a new window for printing
  const handlePrintProceed = async () => {
    try {
      setPrintLoading(true);
      await printHartaPreview({ zon: printZon, kategori_kutipan: printKategori });
    } catch (e) {
      alert("Gagal menjana laporan untuk dicetak. Sila cuba lagi.");
    } finally {
      setPrintLoading(false);
      setPrintDialogOpen(false);
    }
  };

  const [controller] = useMaterialUIController();
  const { darkMode, whiteSidenav, transparentSidenav } = controller;
  const shouldApplyStyle =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

  // ...existing code...

  function handleRowSelect(row) {
    if (!row) {
      setSelectedRow(row);
      return;
    }
    setSelectedRow((prev) => (prev && prev.id === row.id ? null : row));
  }

  // Get table data using the new data hook
  const { columns, rows, pagination } = dataHarta({
    onRowSelect: handleRowSelect,
    selectedRow,
    currentPage,
    itemsPerPage,
    search: debouncedSearch,
    refreshTrigger,
    visibleColumns,
    editingCell,
    editValue,
    onCellEdit: handleCellEdit,
    onSaveEdit: handleSaveEdit,
    onCancelEdit: handleCancelEdit,
    onEditValueChange: setEditValue,
    onEditKeyDown: handleEditKeyDown,
    saving,
    zonOptions,
    kategoriOptions,
  });

  const handleTambahSave = async (data) => {
    try {
      const capData = autoCapitalizePayload(data);
      await apiCreateHarta(capData);
      setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
      setOpenTambah(false);
    } catch (error) {
      handleApiError(error, showError);
    }
  };

  const handleEditClick = () => {
    if (selectedRow) setOpenEdit(true);
  };

  const handleEditSave = async (updatedDataRaw) => {
    try {
      const updatedData = autoCapitalizePayload(updatedDataRaw);
      await apiUpdateHarta(selectedRow.id, updatedData);
      setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
      setOpenEdit(false);
      setSelectedRow(null);
    } catch (error) {
      handleApiError(error, showError);
    }
  };

  const handleDelete = async () => {
    if (selectedRow) {
      // Show confirmation dialog instead of directly deleting
      setOpenDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedRow) {
      try {
        await apiDeleteHarta(selectedRow.id);
        setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
        setSelectedRow(null);
        setOpenDeleteConfirm(false);
      } catch (error) {
        handleApiError(error, showError);
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteConfirm(false);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
  };

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage((prev) => prev + 1);
      setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.error}
                borderRadius="lg"
                coloredShadow="error"
              >
                <MDTypography variant="h6" color="white">
                  Senarai Harta
                </MDTypography>
              </MDBox>
              <MDBox>
                <HartaSearch
                  search={search}
                  handleSearchChange={handleSearchChange}
                  onTambah={() => setOpenTambah(true)}
                  onEdit={handleEditClick}
                  onDelete={handleDelete}
                  onPrint={handlePrintClick}
                  onColumnMenuOpen={handleColumnMenuOpen}
                  selectedRow={selectedRow}
                />

                {/* Column Visibility Menu */}
                <Menu
                  anchorEl={columnMenuAnchor}
                  open={Boolean(columnMenuAnchor)}
                  onClose={handleColumnMenuClose}
                  PaperProps={{
                    sx: {
                      minWidth: 200,
                      '& .MuiMenuItem-root': {
                        px: 2,
                        py: 0.5,
                      },
                    },
                  }}
                >
                  <MenuItem disabled>
                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                      Paparan Lajur
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleColumnToggle('kodharta')}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleColumns.kodharta}
                          size="small"
                          sx={{ py: 0 }}
                        />
                      }
                      label="Kod Harta"
                      sx={{ m: 0, width: '100%' }}
                    />
                  </MenuItem>
                  <MenuItem onClick={() => handleColumnToggle('alamat')}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleColumns.alamat}
                          size="small"
                          sx={{ py: 0 }}
                        />
                      }
                      label="Alamat"
                      sx={{ m: 0, width: '100%' }}
                    />
                  </MenuItem>
                  <MenuItem onClick={() => handleColumnToggle('zonharta')}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleColumns.zonharta}
                          size="small"
                          sx={{ py: 0 }}
                        />
                      }
                      label="Zon"
                      sx={{ m: 0, width: '100%' }}
                    />
                  </MenuItem>
                  <MenuItem onClick={() => handleColumnToggle('kategorikutipan')}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleColumns.kategorikutipan}
                          size="small"
                          sx={{ py: 0 }}
                        />
                      }
                      label="Kategori Kutipan"
                      sx={{ m: 0, width: '100%' }}
                    />
                  </MenuItem>
                  <MenuItem onClick={() => handleColumnToggle('kadarsewa')}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleColumns.kadarsewa}
                          size="small"
                          sx={{ py: 0 }}
                        />
                      }
                      label="Kadar Sewa"
                      sx={{ m: 0, width: '100%' }}
                    />
                  </MenuItem>
                </Menu>

                {/* Print Dialog */}
                <Dialog
                  open={printDialogOpen}
                  onClose={() => setPrintDialogOpen(false)}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle sx={{ ...pageHeaderStyles.error, py: 1.5, px: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" sx={{ color: "#fff" }}>Cetak Senarai Harta</Typography>
                    </Box>
                  </DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" color="text.secondary" mt={2} mb={2}>
                      Pilih kriteria untuk mencetak senarai harta:
                    </Typography>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#ffffffff",
                          fontSize: "0.875rem",
                          p: 1.2,
                          mb: 1,
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                        },
                      }}
                    >
                      <InputLabel>Zon</InputLabel>
                      <Select
                        value={printZon}
                        label="Zon"
                        onChange={(e) => setPrintZon(e.target.value)}
                      >
                        <MenuItem value="">Semua Zon</MenuItem>
                        {zonOptions.map((zon) => (
                          <MenuItem key={zon} value={zon}>
                            {zon}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#ffffffff",
                          fontSize: "0.875rem",
                          p: 1.2,
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                        },
                      }}
                    >
                      <InputLabel>Kategori Kutipan</InputLabel>
                      <Select
                        value={printKategori}
                        label="Kategori Kutipan"
                        onChange={(e) => setPrintKategori(e.target.value)}
                      >
                        <MenuItem value="">Semua Kategori</MenuItem>
                        {kategoriOptions.map((kat) => (
                          <MenuItem key={kat} value={kat}>
                            {kat}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handlePrintProceed}
                      size="small"
                      variant="contained"
                      sx={buttonStyles.primary}
                      startIcon={<PrintIcon />}
                    >
                      CETAK
                    </Button>
                    <Button
                      onClick={() => setPrintDialogOpen(false)}
                      size="small"
                      variant="contained"
                      sx={buttonStyles.cancel}
                    >
                      BATAL
                    </Button>
                  </DialogActions>
                </Dialog>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false}
                />
                <CustomPagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={itemsPerPage}
                  actualItemsOnPage={pagination.actualItemsOnPage}
                  onPageChange={handlePageChange}
                  onNextPage={handleNextPage}
                  onPrevPage={handlePrevPage}
                  variant="contained"
                  color="info"
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <TambahHarta
        open={openTambah}
        onClose={() => setOpenTambah(false)}
        onSave={handleTambahSave}
      />
      <EditHarta
        open={openEdit}
        data={selectedRow}
        onClose={() => setOpenEdit(false)}
        onSave={handleEditSave}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteConfirm} onClose={handleCancelDelete} maxWidth="xs">
        <DialogTitle
          sx={{
            backgroundColor: "#ff0000ff",
            borderBottom: "1px solid #e0e0e0",
            fontSize: "1.1rem",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Pengesahan Padam Harta
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.6,
              color: "#333",
              mb: selectedRow?.kod_harta ? 2 : 0,
              mt: 2,
            }}
          >
            Adakah anda pasti untuk memadam rekod harta ini?
          </Typography>
          {selectedRow?.kod_harta && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                color: "#ff0000",
                fontWeight: "medium",
              }}
            >
              Kod Harta: {selectedRow.kod_harta}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="small"
            style={{
              background: 'linear-gradient(0deg,rgba(0, 99, 15, 1) 0%,rgba(0, 206, 31, 1) 100%)',
              color: '#fff',
            }}
            sx={{
              m: 0,
              px: 2,
              minWidth: 0
            }}
          >
            HAPUS
          </Button>
          <Button
            onClick={handleCancelDelete}
            variant="contained"
            color="success"
            size="small"
            style={{
              background: 'linear-gradient(0deg,rgba(99, 0, 0, 1) 0%,rgba(206, 0, 0, 1) 100%)',
              color: '#fff',
            }}
            sx={{
              m: 0,
              px: 2,
              minWidth: 0
            }}
          >
            BATAL
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
